/**
 * PropertySelectionGrid用のイベントハンドラー
 * 表の変更を地図ポップアップに同期するための処理を集約
 */

import type { PropertySelectionRow } from "../types";
import { isActivePopup } from "./tagSync/activePopupManager";
import { dispatchMapClickTagUpdate } from "./tagSync/mapClickTagSync";
import { dispatchRealEstateSearchTagUpdate } from "./tagSync/realEstateSearchTagSync";
import { dispatchReceptionBookTagUpdate } from "./tagSync/receptionBookTagSync";
import { dispatchRequestedPicturesTagUpdate } from "./tagSync/requestedPicturesTagSync";
import { dispatchMapClickRemarksUpdate } from "./tagSync/mapClickRemarksSync";
import { dispatchRealEstateSearchRemarksUpdate } from "./tagSync/realEstateSearchRemarksSync";
import { dispatchReceptionBookRemarksUpdate } from "./tagSync/receptionBookRemarksSync";
import { dispatchRequestedPicturesRemarksUpdate } from "./tagSync/requestedPicturesRemarksSync";
import { dispatchMapClickAdditionalFieldsUpdate } from "./tagSync/mapClickAdditionalFieldsSync";
import { dispatchRealEstateSearchAdditionalFieldsUpdate } from "./tagSync/realEstateSearchAdditionalFieldsSync";
import { dispatchReceptionBookAdditionalFieldsUpdate } from "./tagSync/receptionBookAdditionalFieldsSync";
import { dispatchRequestedPicturesAdditionalFieldsUpdate } from "./tagSync/requestedPicturesAdditionalFieldsSync";

/**
 * アクティブなポップアップかどうかをチェック
 */
const checkActivePopup = (row: PropertySelectionRow): boolean => {
  if (!row.pinType) return false;

  return isActivePopup(
    row.location,
    row.chibanKaokuNumber,
    row.prefName,
    row.pinType
  );
};

/**
 * 表題部検索の配列インデックスを取得する関数の型
 * 表題部検索ピン以外の場合はnullを返す
 */
type FindRealEstateSearchIndex = (row: PropertySelectionRow) => number | null;

/**
 * 受付帳の配列インデックスを取得する関数の型
 * 受付帳ピン以外の場合はnullを返す
 */
type FindReceptionBookIndex = (row: PropertySelectionRow) => number | null;

/**
 * PropertySelectionGrid用のイベントハンドラーを作成
 *
 * @param findRealEstateSearchIndex - 表題部検索ピン用：配列内のインデックスを取得する関数
 * @param findReceptionBookIndex - 受付帳ピン用：配列内のインデックスを取得する関数
 * @returns タグ、備考、追加項目の更新イベントハンドラー
 */
export const createPropertySelectionEventHandlers = (
  findRealEstateSearchIndex: FindRealEstateSearchIndex,
  findReceptionBookIndex: FindReceptionBookIndex
): {
  handleTagUpdate: (row: PropertySelectionRow, tags: string[]) => void;
  handleRemarksUpdate: (row: PropertySelectionRow, remarks: string) => void;
  handleAdditionalFieldsUpdate: (
    row: PropertySelectionRow,
    additionalFields: string
  ) => void;
} => {
  return {
    handleTagUpdate: (row: PropertySelectionRow, tags: string[]): void => {
      if (!checkActivePopup(row)) return;

      switch (row.pinType) {
        case "map-click":
          dispatchMapClickTagUpdate(tags);
          break;
        case "real-estate-search": {
          // 表題部検索は複数存在するため、配列内のインデックスが必要
          const index = findRealEstateSearchIndex(row);
          if (index !== null) {
            dispatchRealEstateSearchTagUpdate(index, tags);
          }
          break;
        }
        case "reception-book": {
          const index = findReceptionBookIndex(row);
          if (index !== null) {
            dispatchReceptionBookTagUpdate(index, tags);
          }
          break;
        }
        case "requested-pictures":
          dispatchRequestedPicturesTagUpdate(tags);
          break;
      }
    },

    handleRemarksUpdate: (row: PropertySelectionRow, remarks: string): void => {
      if (!checkActivePopup(row)) return;

      switch (row.pinType) {
        case "map-click":
          dispatchMapClickRemarksUpdate(remarks);
          break;
        case "real-estate-search": {
          // 表題部検索は複数存在するため、配列内のインデックスが必要
          const index = findRealEstateSearchIndex(row);
          if (index !== null) {
            dispatchRealEstateSearchRemarksUpdate(index, remarks);
          }
          break;
        }
        case "reception-book": {
          const index = findReceptionBookIndex(row);
          if (index !== null) {
            dispatchReceptionBookRemarksUpdate(index, remarks);
          }
          break;
        }
        case "requested-pictures":
          dispatchRequestedPicturesRemarksUpdate(remarks);
          break;
      }
    },

    handleAdditionalFieldsUpdate: (
      row: PropertySelectionRow,
      additionalFields: string
    ): void => {
      if (!checkActivePopup(row)) return;

      switch (row.pinType) {
        case "map-click":
          dispatchMapClickAdditionalFieldsUpdate(additionalFields);
          break;
        case "real-estate-search": {
          // 表題部検索は複数存在するため、配列内のインデックスが必要
          const index = findRealEstateSearchIndex(row);
          if (index !== null) {
            dispatchRealEstateSearchAdditionalFieldsUpdate(
              index,
              additionalFields
            );
          }
          break;
        }
        case "reception-book": {
          const index = findReceptionBookIndex(row);
          if (index !== null) {
            dispatchReceptionBookAdditionalFieldsUpdate(
              index,
              additionalFields
            );
          }
          break;
        }
        case "requested-pictures":
          dispatchRequestedPicturesAdditionalFieldsUpdate(additionalFields);
          break;
      }
    },
  };
};
